import React from "react";
import { Modal, Button, Form } from "@weezevent/nacre";
import PropTypes from "prop-types";
import { Trans, useTranslation } from "react-i18next";
import { Grid } from "semantic-ui-react";
import useWeezTags from "api/api-hooks/use-weez-tags";
import SingleValueTreeSelect from "components/form-helpers/SingleValueTreeSelect";
import Style from "./link-weeztag-to-checklists.module.scss";
import { ActionsTypes } from "../../constants";

const createSelectHierarchyOptionsForPopulationTag = data => {
	if (!data || !Array.isArray(data)) {
		return [];
	}
	const categoriesMap = {};

	data.forEach(item => {
		if (!categoriesMap[item.category_name]) {
			categoriesMap[item.category_name] = {
				value: item.category_name,
				text: item.category_name,
				children: []
			};
		}

		categoriesMap[item.category_name].children.push({
			value: item.id,
			text: item.category_name + " - " + item.period_name
		});
	});

	return Object.values(categoriesMap);
};

export const LinkWeezTagToChecklists = ({
	onSubmit,
	onCancel,
	isSubmitting,
	checklistsIds
}) => {
	const { t } = useTranslation();

	const { data: weezTagsData } = useWeezTags({
		params: { type: "CHECKLIST" }
	});

	const options = React.useMemo(
		() => createSelectHierarchyOptionsForPopulationTag(weezTagsData),
		[weezTagsData]
	);

	const [selectedTagId, setSelectedTagId] = React.useState();

	const handleSelectChange = React.useCallback(optionValue => {
		setSelectedTagId(optionValue);
	}, []);

	const handleSubmit = () => {
		if (!selectedTagId) return;

		const config = checklistsIds.map(checklistId => ({
			checkpoint_id: checklistId,
			population_tag_id: selectedTagId
		}));

		onSubmit({
			actionType: ActionsTypes.LINK_WEEZ_TAG_TO_CHECKLISTS,
			config
		});
	};

	return (
		<Modal
			onClose={onCancel}
			title={
				<Trans i18nKey="access.checkpoint.batchChecklists.linkWeezTagToChecklists.modal.title">
					Apply one population tag
				</Trans>
			}
			size="big"
			cancelButton={
				<Button disabled={isSubmitting} onClick={onCancel}>
					{t("access.common.cancel")}
				</Button>
			}
			submitButton={
				<Button
					disabled={isSubmitting || !selectedTagId}
					onClick={handleSubmit}
					primary
				>
					<Trans i18nKey="access.checkpoint.batchChecklists.linkWeezTagToChecklists.modal.applyCta">
						Apply
					</Trans>
				</Button>
			}
		>
			<div>
				<Form>
					<Form.Field>
						<Grid.Row>
							<label className={Style.inputLabelDescription}>
								{t(
									"access.checkpoint.batchChecklists.linkWeezTagToChecklists.modal.description"
								)}
							</label>
							<SingleValueTreeSelect
								onChange={handleSelectChange}
								data={options}
								value={selectedTagId || null}
								placeholder={t(
									"access.checkpoint.batchChecklists.linkWeezTagToChecklists.modal.placeholder",
									{
										count: weezTagsData?.length
									}
								)}
								className={Style.populationTagInputSelector}
							/>
						</Grid.Row>
					</Form.Field>
				</Form>
			</div>
		</Modal>
	);
};

LinkWeezTagToChecklists.propTypes = {
	onSubmit: PropTypes.func,
	onCancel: PropTypes.func,
	isSubmitting: PropTypes.bool,
	checklistsIds: PropTypes.array
};

LinkWeezTagToChecklists.defaultProps = {
	onSubmit: () => {},
	onCancel: () => {},
	isSubmitting: false,
	checklistsIds: []
};
